import React, { useEffect, useState } from "react";
import { MapContainer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet-minimap/dist/Control.MiniMap.min.css";
import MiniMapControl from "./MiniMapControl";
import "../assets/css/MapView.css";
import EndemicityLevels from "./EndemicityLevels";
import {
  getGeoKebele,
  getGeoRegionalState,
  getGeoWoreda,
  getGeoZone,
} from "../Services/apiService.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
export const spinnerOverlayStyle = {
  position: "absolute",
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

export const spinnerStyle = {
  width: "45px",
  height: "45px",
  border: "3px solid #f3f3f3",
  borderTop: "3px solid #024795",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const generateRegionColors = (features) => {
  const colors = [
    "#ff6666",
    "#66ccff",
    "#ffcc66",
    "#66ff99",
    "#cc66ff",
    "#ff99cc",
    "#99ffcc",
    "#ffcc99",
    "#66ffff",
    "#ff9966",
  ];
  const mapping = {};
  features.forEach((f, idx) => {
    mapping[f.properties.shapeName] = colors[idx % colors.length];
  });
  return mapping;
};

const EthiopiaMask = ({
  ethiopiaRegions,
  selectedRegionalState,
  selectedZone,
  selectedWoreda,
  selectedKebele,
  ethiopiaZones,
  ethiopiaWoredas,
  ethiopiaKebeles,
  onRegionClick,
  onZoneClick,
  onWoredaClick,
  onKebeleClick,
}) => {
  const map = useMap();
  const regionColors = generateRegionColors(ethiopiaRegions);
  const brightenColor = (hex, percent = 20) => {
    let num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + percent;
    let g = ((num >> 8) & 0x00ff) + percent;
    let b = (num & 0x0000ff) + percent;

    r = Math.min(255, r);
    g = Math.min(255, g);
    b = Math.min(255, b);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const darkenColor = (hex, percent = 20) => {
    let num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) - percent;
    let g = ((num >> 8) & 0x00ff) - percent;
    let b = (num & 0x0000ff) - percent;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const styleFeature = (feature, type) => {
    const props = feature.properties;
    if (type === "region") {
      const baseColor = regionColors[props.shapeName];
      const mutedColor = "rgba(136, 135, 135, 0.07)";
      if (!selectedRegionalState) {
        return {
          color: "#333",
          weight: 1,
          fillColor: baseColor,
          fillOpacity: 0.4,
        };
      }
      if (props.shapeName === selectedRegionalState) {
        return {
          color: darkenColor(baseColor, 30),
          weight: 3,
          fillColor: brightenColor(baseColor, 30),
          fillOpacity: 0.9,
          className: "region-elevated",
        };
      } else {
        return {
          color: "#888",
          weight: 1,
          fillColor: mutedColor,
          fillOpacity: 0.3,
        };
      }
    }
    let baseStyles = {
      zone: {
        color: "#ff8800",
        weight: 1.2,
        fillColor: "#ffe5cc",
        fillOpacity: 0.25,
      },
      woreda: {
        color: "#009933",
        weight: 1.5,
        fillColor: "#ccffcc",
        fillOpacity: 0.25,
      },
      kebele: {
        color: "#9900cc",
        weight: 1.5,
        fillColor: "#e5ccff",
        fillOpacity: 0.6,
      },
    };
    return baseStyles[type];
  };
  const onEachFeature = (feature, layer, type) => {
    const props = feature.properties;
    const baseColor = regionColors[props.shapeName];

    layer.on("mouseover", () => {
      layer.setStyle({
        fillOpacity: 1,
        weight: 1.5,
        className: "hover-shadow"
      });
    });
    layer.on("mouseout", () => {
      if (layer.defaultOptions?.style) {
        layer.setStyle(layer.defaultOptions.style);
      } else {
        layer.setStyle({
          weight: 1,
          fillOpacity: 0.4,
        });
      }
    });

    if (type === "region") {
      if (!selectedRegionalState || props.shapeName === selectedRegionalState) {
        layer.bindTooltip(`<b>Region:</b> ${props.shapeName}`, {
          permanent: true,
          direction: "center",
          className: "map-label",
          offset: [0, 0],
        });
        layer.on("add", () => {
          try {
            const centroid = layer.getBounds().getCenter();
            const tooltip = layer.getTooltip(); 
            if (tooltip && typeof tooltip.setLatLng === "function") {
              tooltip.setLatLng(centroid);
            } else {
              layer.openTooltip(centroid);
            }
          } catch (err) {
          }
        });
        map.on("zoomend moveend", () => {
          const centroid = layer.getBounds().getCenter();
          const tooltip = layer.getTooltip();
          if (tooltip && typeof tooltip.setLatLng === "function") {
            tooltip.setLatLng(centroid);
          }
        });
      } else {
        layer.unbindTooltip();
      }
    }

    if (type === "zone") {
      layer.bindTooltip(
        `<div>
        <b>Zone:</b> ${props.shapeName}<br/>
        <b>Region:</b> ${props.REGIONNAME || selectedRegionalState || ""}
      </div>`,
        {
          permanent: true,
          direction: "center",
          className: "map-label",
        }
      );
    }

    if (type === "woreda") {
      layer.bindTooltip(`<b>${props.shapeName}</b>`, {
        permanent: true,
        direction: "center",
        className: "map-label",
      });
      layer.bindPopup(
        `<div>
      <b>Woreda:</b> ${props.shapeName}<br/>
      <b>Zone:</b> ${props.ZONENAME || selectedZone || ""}<br/>
      <b>Region:</b> ${props.REGIONNAME || selectedRegionalState || ""}
    </div>`
      );
      layer.on("click", () => {
        if (onWoredaClick) onWoredaClick(props.shapeName);
      });
    }

    if (type === "kebele") {
      layer.bindTooltip(`<b>${props.shapeName}</b>`, {
        permanent: true,
        direction: "center",
        className: "map-label",
      });
      layer.bindPopup(
        `<div>
      <b>Kebele:</b> ${props.shapeName}<br/>
      <b>Woreda:</b> ${props.WOREDANAME || selectedWoreda || ""}<br/>
      <b>Zone:</b> ${props.ZONENAME || selectedZone || ""}<br/>
      <b>Region:</b> ${props.REGIONNAME || selectedRegionalState || ""}
    </div>`
      );
      layer.on("click", () => {
        if (onKebeleClick) onKebeleClick(props.shapeName);
      });
    }

    layer.on("click", () => {
      if (type === "region" && onRegionClick) onRegionClick(props.shapeName);
      if (type === "zone" && onZoneClick) onZoneClick(props.shapeName);
      if (type === "woreda" && onWoredaClick) onWoredaClick(props.shapeName);
      if (type === "kebele" && onKebeleClick) onKebeleClick(props.shapeName);
    });
  };
  useEffect(() => {
    map.eachLayer((l) => {
      if (!(l instanceof L.TileLayer)) map.removeLayer(l);
    });
    const regionsToShow = ethiopiaRegions;

    const regionLayer = L.geoJSON(
      { type: "FeatureCollection", features: regionsToShow },
      {
        style: (f) => styleFeature(f, "region"),
        onEachFeature: (f, l) => {
          l.defaultOptions = { style: styleFeature(f, "region") };
          onEachFeature(f, l, "region")
        },
      }
    ).addTo(map);
    let zoneLayer;
    if (selectedRegionalState && ethiopiaZones.length > 0) {
      const zonesToShow = ethiopiaZones.map((z) => ({
        type: "Feature",
        geometry: z.geometry,
        properties: { ...z.properties },
      }));
      zoneLayer = L.geoJSON(
        { type: "FeatureCollection", features: zonesToShow },
        {
          style: (f) => styleFeature(f, "zone"),
          onEachFeature: (f, l) => {
            l.defaultOptions = { style: styleFeature(f, "zone") };
            onEachFeature(f, l, "zone")
          },
        }
      ).addTo(map);
    }
    let woredaLayer, kebeleLayer;
    if (selectedZone && ethiopiaWoredas.length > 0) {
      if (zoneLayer) {
        map.removeLayer(zoneLayer);
        zoneLayer = null;
      }
      const woredasToShow = ethiopiaWoredas.map((z) => ({
        type: "Feature",
        geometry: z.geometry,
        properties: { ...z.properties },
      }));
      woredaLayer = L.geoJSON(
        { type: "FeatureCollection", features: woredasToShow },
        {
          style: (f) => styleFeature(f, "woreda"),
          onEachFeature: (f, l) => {
            l.defaultOptions = { style: styleFeature(f, "woreda") };
            onEachFeature(f, l, "woreda")
          },
        }
      ).addTo(map);
    }
    if (selectedWoreda && ethiopiaKebeles.length > 0) {
      if (woredaLayer) {
        map.removeLayer(woredaLayer);
        woredaLayer = null;
      }
      let kebelesToShow = [];
      if (!selectedKebele) {
        kebelesToShow = ethiopiaKebeles.map((k) => ({
          type: "Feature",
          geometry: k.geometry,
          properties: { ...k.properties },
        }));
      }

      else {
        const filtered = ethiopiaKebeles.filter(
          (k) =>
            k.properties.shapeName === selectedKebele ||
            k.properties.KebeleName === selectedKebele ||
            k.properties.kebele === selectedKebele
        );

        kebelesToShow = filtered.map((k) => ({
          type: "Feature",
          geometry: k.geometry,
          properties: { ...k.properties },
        }));
      }
      kebeleLayer = L.geoJSON(
        { type: "FeatureCollection", features: kebelesToShow },
        {
          style: (f) => styleFeature(f, "kebele"),
          onEachFeature: (f, l) => {
            l.defaultOptions = { style: styleFeature(f, "kebele") };
            onEachFeature(f, l, "kebele")
          },
        }
      ).addTo(map);
    }
    let fitLayer =
      kebeleLayer ||
      woredaLayer ||
      zoneLayer ||
      regionLayer;

    if (fitLayer && fitLayer.getBounds().isValid()) {
      map.flyToBounds(fitLayer.getBounds(), {
        padding: [30, 30],
        duration: 1.4,
      });
    }

    return () => {
      if (regionLayer) map.removeLayer(regionLayer);
      if (zoneLayer) map.removeLayer(zoneLayer);
      if (woredaLayer) map.removeLayer(woredaLayer);
      if (kebeleLayer) map.removeLayer(kebeleLayer);
    };
  }, [
    map,
    selectedRegionalState,
    selectedZone,
    selectedWoreda,
    selectedKebele,
    ethiopiaZones,
    ethiopiaWoredas,
    ethiopiaKebeles,
  ]);

  return null;
};

export default function MapView({
  selectedRegionalState,
  selectedZone,
  selectedWoreda,
  selectedKebele,
  onRegionClick,
  onZoneClick,
  onWoredaClick,
  onKebeleClick,
  missingZoneGEOJSON,
  missingWoredaGEOJSON,
}) {
  const [ethiopiaZones, setEthiopiaZones] = useState(null);
  const [ethiopiaWoredas, setEthiopiaWoredas] = useState(null);
  const [ethiopiaKebeles, setEthiopiaKebeles] = useState(null);
  const [ethiopiaRegions, setEthiopiaRegions] = useState(null);
  const [showZoneNotification, setShowZoneNotification] = useState(false);
  const [showWoredaNotification, setShowWoredaNotification] = useState(false);

  useEffect(() => {
    if (missingZoneGEOJSON) {
      setShowZoneNotification(true);
      const timer = setTimeout(() => setShowZoneNotification(false), 3500);
      return () => clearTimeout(timer);
    }
  }, [missingZoneGEOJSON, selectedZone]);

  useEffect(() => {
    if (missingWoredaGEOJSON) {
      setShowWoredaNotification(true);
      const timer = setTimeout(() => setShowWoredaNotification(false), 3500);
      return () => clearTimeout(timer);
    }
  }, [missingWoredaGEOJSON, selectedWoreda]);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionData = await getGeoRegionalState();
        setEthiopiaRegions(regionData);
      } catch (error) {
        console.error("Region fetch error:", error);
        toast.error("Error while fetching Region!");
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, []);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const zoneData = await getGeoZone(selectedRegionalState);
        setEthiopiaZones(zoneData);
      } catch (error) {
        console.error("Zone fetch error:", error);
        toast.error("Error while fetching Zones!");
      }
    };
    if (selectedRegionalState) {
      fetchZones();
    }
  }, [selectedRegionalState]);
  useEffect(() => {
    const fetchWoredas = async () => {
      try {
        const woredaData = await getGeoWoreda(selectedZone);
        setEthiopiaWoredas(woredaData);
      } catch (error) {
        console.error("Woreda fetch error:", error);
        toast.error("Error while fetching Woreda!");
      }
    };
    if (selectedRegionalState && selectedZone) {
      fetchWoredas();
    }
  }, [selectedRegionalState, selectedZone]);
  useEffect(() => {
    const fetchKebeles = async () => {
      try {
        const kebeleData = await getGeoKebele(selectedWoreda);
        setEthiopiaKebeles(kebeleData);
      } catch (error) {
        console.error("Kebele fetch error:", error);
        toast.error("Error while fetching Kebele!");
      }
    };
    if (selectedRegionalState && selectedZone && selectedWoreda) {
      fetchKebeles();
    }
  }, [selectedRegionalState, selectedZone, selectedWoreda]);

  const isLoading =
    loading ||
    !ethiopiaRegions ||
    (selectedRegionalState && !ethiopiaZones) ||
    (selectedZone && !ethiopiaWoredas) ||
    (selectedWoreda && !ethiopiaKebeles);
  return isLoading ? (
    <div style={spinnerOverlayStyle}>
      <div style={spinnerStyle}></div>
    </div>
  ) : (
    <MapContainer
      style={{ height: "100%", width: "100%" }}
      zoom={6}
      center={[9.145, 40.4897]}
    >
      {showZoneNotification && (
        <div className="custom-notification">
          <span> No Zone Map Found</span>
          <button
            className="close-btn"
            onClick={() => setShowZoneNotification(false)}
          >
            &times;
          </button>
        </div>
      )}

      {showWoredaNotification && (
        <div className="custom-notification">
          <span> No Woreda Map Found</span>
          <button
            className="close-btn"
            onClick={() => setShowWoredaNotification(false)}
          >
            &times;
          </button>
        </div>
      )}

      <EndemicityLevels
        selectedRegionalState={selectedRegionalState}
        selectedZone={selectedZone}
        selectedWoreda={selectedWoreda}
        selectedKebele={selectedKebele}
      />
      <EthiopiaMask
        selectedRegionalState={selectedRegionalState}
        selectedZone={selectedZone}
        selectedWoreda={selectedWoreda}
        selectedKebele={selectedKebele}
        ethiopiaRegions={ethiopiaRegions}
        ethiopiaZones={ethiopiaZones}
        ethiopiaWoredas={ethiopiaWoredas}
        ethiopiaKebeles={ethiopiaKebeles}
        onRegionClick={onRegionClick}
        onZoneClick={onZoneClick}
        onWoredaClick={onWoredaClick}
        onKebeleClick={onKebeleClick}
      />
      <MiniMapControl
        ethiopiaRegions={ethiopiaRegions}
        selectedRegionalState={selectedRegionalState}
      />
    </MapContainer>
  );
}
